import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { UserNotification } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { 
  Bell, 
  Inbox, 
  CheckCircle, 
  Mail, 
  FileText, 
  Clock, 
  AlertTriangle,
  MessageCircle,
  Bell as BellIcon,
  CheckSquare,
  Calendar,
  Users,
  Ticket
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Skeleton } from "../components/ui/skeleton.jsx";
import { Badge } from "../components/ui/badge.jsx";

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      
      const userNotifications = await UserNotification.filter({
        user_id: userData.id
      });
      
      // Ordenar por data de criação (mais recentes primeiro)
      userNotifications.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
      
      setNotifications(userNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
    setIsLoading(false);
  };

  const handleNotificationClick = async (notification) => {
    // Marcar como lida se não estiver
    if (!notification.read) {
      try {
        await UserNotification.update(notification.id, { read: true });
        
        // Atualizar estado local
        setNotifications(notifications.map(n => 
          n.id === notification.id ? { ...n, read: true } : n
        ));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    
    // Navegar para a URL de ação se existir
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Atualizar todas as notificações não lidas
      const unreadNotifications = notifications.filter(n => !n.read);
      
      for (const notification of unreadNotifications) {
        await UserNotification.update(notification.id, { read: true });
      }
      
      // Atualizar estado local
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getTimeAgo = (date) => {
    return formatDistanceToNow(new Date(date), { locale: ptBR, addSuffix: true });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'ticket':
        return <Ticket className="h-5 w-5 text-blue-500" />;
      case 'message':
        return <MessageCircle className="h-5 w-5 text-purple-500" />;
      case 'project':
        return <CheckSquare className="h-5 w-5 text-green-500" />;
              case 'system':
          return <BellIcon className="h-5 w-5 text-red-500" />;
      case 'user':
        return <Users className="h-5 w-5 text-indigo-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Notificações</h1>
          <p className="text-muted-foreground mt-1">
            Mantenha-se informado sobre atualizações importantes
          </p>
        </div>
        
        {notifications.some(n => !n.read) && (
          <Button variant="outline" onClick={markAllAsRead}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Marcar todas como lidas
          </Button>
        )}
      </div>

      <Tabs defaultValue="all" onValueChange={setFilter}>
        <TabsList className="grid grid-cols-3 md:grid-cols-7 mb-4">
          <TabsTrigger value="all">
            Todas
            {notifications.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {notifications.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="unread">
            Não lidas
            {notifications.filter(n => !n.read).length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {notifications.filter(n => !n.read).length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="ticket">Chamados</TabsTrigger>
          <TabsTrigger value="message">Mensagens</TabsTrigger>
          <TabsTrigger value="project">Projetos</TabsTrigger>
          
          <TabsTrigger value="system">Sistema</TabsTrigger>
        </TabsList>

        <TabsContent value={filter}>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-1/4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredNotifications.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Bell className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-3" />
                <h3 className="text-lg font-medium">Nenhuma notificação encontrada</h3>
                <p className="text-muted-foreground mt-2">
                  {filter === 'all' 
                    ? "Você não tem notificações no momento." 
                    : filter === 'unread'
                      ? "Você não tem notificações não lidas."
                      : `Você não tem notificações do tipo ${filter}.`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification) => (
                <Card 
                  key={notification.id}
                  className={`hover:shadow-md transition-shadow cursor-pointer ${!notification.read ? 'border-l-4 border-l-primary' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${!notification.read ? 'bg-primary/10' : 'bg-secondary'}`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-lg">{notification.title}</h4>
                          <div className="flex items-center gap-2">
                            {notification.priority && (
                              <Badge className={getPriorityColor(notification.priority)}>
                                {notification.priority === 'high' ? 'Alta' : 
                                 notification.priority === 'medium' ? 'Média' : 'Baixa'}
                              </Badge>
                            )}
                            {!notification.read && (
                              <Badge className="bg-primary text-primary-foreground">Nova</Badge>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-muted-foreground mt-1">
                          {notification.description}
                        </p>
                        
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-muted-foreground">
                            {getTimeAgo(notification.created_date)}
                          </span>
                          
                          {notification.action_url && (
                            <Button variant="ghost" size="sm" className="h-auto py-1">
                              Ver detalhes
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}